if (!customElements.get('mo-shop-purchase')) {
  customElements.define('mo-shop-purchase', class extends HTMLElement {
    static selectOffer(offers, preferredId, rememberedId) {
      const remembered = offers.find(offer => offer.id === rememberedId && offer.available);
      if (remembered) return remembered;
      const preferred = offers.find(offer => offer.id === preferredId);
      return preferred?.available ? preferred : null;
    }

    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.variant = this.querySelector('[data-shop-variant]');
      this.nameInput = this.querySelector('[data-shop-name]');
      this.editions = this.querySelector('[data-shop-editions]');
      this.drafts = new Map();
      this.nameInput.addEventListener('keydown', event => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        const buttons = [...this.editions.querySelectorAll('button:not(:disabled)')];
        (buttons.find(button => button.value === this.variant.value) || buttons[0])?.focus();
      });
      this.onDocumentClick = event => {
        const trigger = event.target.closest('[data-shop-open]');
        if (!trigger || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const card = trigger.closest('[data-shop-buy]');
        if (!card) return;
        event.preventDefault();
        if (this.form.getAttribute('aria-busy') !== 'true') this.open(card, trigger);
      };
      document.addEventListener('click', this.onDocumentClick);
      this.querySelector('[data-shop-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('cancel', () => { this.escapeCancelled = true; });
      this.onDocumentKeyUp = event => {
        if (!this.escapeCancelled) return;
        this.escapeCancelled = false;
        // Nach dem Fokuswechsel darf dieselbe Escape-Taste die Tasche nicht mit schließen.
        if (event.key === 'Escape') event.stopPropagation();
      };
      document.addEventListener('keyup', this.onDocumentKeyUp, true);
      this.dialog.addEventListener('close', () => {
        this.remember();
        if (!document.querySelector('cart-drawer.active') && !document.querySelector('dialog[open]') && !this.overflowWasActive) document.body.classList.remove('overflow-hidden');
        if (this.added) document.querySelector('cart-drawer.active .drawer__close')?.focus({ preventScroll: true });
        else if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      });
      this.form.addEventListener('submit', event => {
        const offer = this.product.offers.find(item => item.id === event.submitter?.value && item.available);
        if (!offer) { event.preventDefault(); return; }
        this.update(offer);
        this.remember();
      }, true);
      this.addEventListener('mo:cart-added', () => {
        if (!this.dialog.open) return;
        this.added = true;
        this.dialog.close();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keyup', this.onDocumentKeyUp, true);
      if (this.dialog?.open) this.dialog.close();
    }

    remember() {
      if (!this.product) return;
      this.drafts.set(this.product.productId, { edition: this.variant.value, name: this.product.personalization === false ? '' : this.nameInput.value });
    }

    open(card, trigger) {
      this.product = JSON.parse(card.querySelector('[data-shop-offers]').textContent);
      this.trigger = trigger;
      this.added = false;
      this.overflowWasActive = document.body.classList.contains('overflow-hidden');
      const draft = this.drafts.get(this.product.productId);
      const personalizable = this.product.personalization !== false;
      this.nameInput.value = personalizable ? draft?.name || '' : '';
      this.nameInput.disabled = !personalizable;
      this.querySelector('[data-shop-title]').textContent = this.product.title;
      this.querySelector('[data-shop-choice-label]').textContent = personalizable ? this.dataset.editionLabel : this.dataset.variantLabel;
      this.form.querySelector('[role="alert"]').hidden = true;
      this.editions.querySelectorAll('button').forEach(button => button.remove());
      for (const offer of this.product.offers) {
        const button = document.createElement('button');
        button.type = 'submit';
        button.name = 'id';
        button.value = offer.id;
        button.className = 'mo-shop-purchase__edition';
        button.disabled = !offer.available;
        const description = document.createElement('span');
        description.className = 'mo-shop-purchase__edition-name';
        const name = document.createElement('span');
        name.textContent = offer.label;
        const status = document.createElement('small');
        const samePrice = this.product.offers.every(item => item.price === offer.price);
        status.textContent = personalizable || !samePrice ? offer.money : '';
        if (!offer.available || offer.stock !== this.dataset.availableLabel) status.textContent += (status.textContent ? ' · ' : '') + offer.stock;
        status.hidden = !status.textContent;
        description.append(name, status);
        const action = document.createElement('span');
        action.setAttribute('data-cart-add-label', '');
        action.className = 'mo-shop-purchase__edition-action';
        action.textContent = offer.available ? this.dataset.addLabel : offer.stock;
        button.setAttribute('aria-label', offer.label + ' – ' + action.textContent);
        button.append(description, action);
        button.addEventListener('focus', () => this.update(offer.available ? offer : null));
        this.editions.append(button);
      }
      this.update(this.constructor.selectOffer(this.product.offers, this.product.preferredId, draft?.edition));
      const personalization = this.querySelector('[data-shop-personalization]');
      personalization.hidden = !personalizable;
      personalization.open = personalizable && (trigger.hasAttribute('data-shop-dedication') || this.nameInput.value.length > 0);
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      if (trigger.hasAttribute('data-shop-dedication') && personalizable) this.nameInput.focus();
      else this.querySelector('h2').focus({ preventScroll: true });
    }

    update(offer) {
      const fallback = this.product.offers.find(item => item.id === this.product.preferredId) || this.product.offers[0];
      const display = offer || fallback;
      this.variant.value = offer?.id || '';
      this.variant.disabled = !offer;
      this.querySelector('[data-shop-price]').textContent = display?.money || '';
      this.querySelector('[data-shop-variant-label]').textContent = this.product.personalization === false ? display?.label || '' : '';
      this.querySelector('[data-shop-note]').textContent = offer?.note || '';
      const image = this.querySelector('[data-shop-image]');
      image.hidden = !display?.image;
      if (display?.image) { image.src = display.image; image.alt = display.imageAlt || this.product.title; }
      const details = this.querySelector('[data-shop-details]');
      details.href = display?.url || this.product.url;
      details.textContent = this.product.personalization === false ? this.dataset.productDetailsLabel : this.dataset.detailsLabel;
      this.form.querySelector('[role="alert"]').hidden = true;
    }
  });
}
